import type { Mutation } from '@tanstack/react-query';
import { toast } from 'sonner';

import { ApiError } from '@/shared/api/client';
import { isPlanGateError } from '@/shared/api/plan-errors';
import { errorText } from '@/shared/i18n/error-text';
import { dispatchUpgradePrompt, type UpgradePromptDetail } from '@/widgets/billing/upgrade-prompt';
import { handlePlanGateError } from '@/widgets/billing/plan-required-toast';

/**
 * ONE RULE for mutation errors: a failed mutation shows exactly ONE toast.
 *
 *  1. The mutation owns its error when it sets `meta: { silentErrors: true }`
 *     (aliases `silentError` / `handlesError`) — the global handler shows
 *     nothing (it draws an inline error / locked state itself).
 *  2. The mutation also owns its error when its own `onError` (hook-level,
 *     `mutate(v, { onError })`, or a `mutateAsync` try/catch) shows a toast.
 *     The cache can't see call-level callbacks, so instead of guessing, the
 *     global toast waits one macrotask (every local handler has run by then —
 *     React Query calls them in the same microtask chain) and stands down if
 *     a local handler toasted. An `onError` that only rolls back an optimistic
 *     update shows no toast, so the user still gets the global one — a failure
 *     is never silent.
 *  3. Otherwise the global handler shows one friendly line (`errorText`, never
 *     the machine code). A 402 plan gate becomes the "Available on the … plan"
 *     upgrade toast instead — and if a local handler already toasted the plain
 *     text for it, that toast is swapped for the upgrade one (still one toast).
 *
 * Free-tier limits (FREE_TIER_*) open the upgrade DIALOG immediately, even for
 * silent mutations — it is a dialog, not an error toast, and this central
 * handler is the only place that dispatches it.
 */

export type MutationErrorMeta = { silentErrors?: boolean; silentError?: boolean; handlesError?: boolean };

const FREE_TIER_CODES = new Set(['FREE_TIER_USERS', 'FREE_TIER_PROJECTS', 'FREE_TIER_STORAGE', 'FREE_TIER_FILE_SIZE']);

/** True when the mutation declared that it renders its own error UI. */
export function isSilentMutation(meta: Record<string, unknown> | undefined): boolean {
  const m = (meta ?? {}) as MutationErrorMeta;
  return m.silentErrors === true || m.silentError === true || m.handlesError === true;
}

/** Toasts a local handler can have raised for this failure (not success/loading). */
function newFeedbackToasts(fromIndex: number): Array<string | number> {
  return toast
    .getHistory()
    .slice(fromIndex)
    .filter((t) => {
      if ('dismiss' in t && t.dismiss) return false;
      const type = (t as { type?: string }).type;
      return type !== 'success' && type !== 'loading';
    })
    .map((t) => t.id);
}

export function createMutationErrorHandler(opts: {
  openBilling: () => void;
  /** Defers the global toast until local handlers have run. Tests may inject. */
  defer?: (fn: () => void) => void;
}) {
  const defer = opts.defer ?? ((fn: () => void) => void setTimeout(fn, 0));

  return (
    error: unknown,
    _variables: unknown,
    _context: unknown,
    mutation: Pick<Mutation<unknown, unknown, unknown, unknown>, 'meta'>,
  ): void => {
    if (error instanceof ApiError && error.code && FREE_TIER_CODES.has(error.code)) {
      const body = error.body.error as Partial<UpgradePromptDetail> & { planCode?: string };
      dispatchUpgradePrompt({
        limitCode: error.code as UpgradePromptDetail['limitCode'],
        planCode: body.planCode ?? 'free',
        current: typeof body.current === 'number' ? body.current : 0,
        max: typeof body.max === 'number' ? body.max : 0,
      });
      return;
    }

    if (isSilentMutation(mutation.meta)) return;

    const mark = toast.getHistory().length;
    defer(() => {
      const local = newFeedbackToasts(mark);
      if (isPlanGateError(error)) {
        for (const id of local) toast.dismiss(id);
        handlePlanGateError(error, { silent: false, openBilling: opts.openBilling });
        return;
      }
      if (local.length > 0) return; // the mutation's own handler already told the user
      if (error instanceof ApiError && error.code) {
        console.warn(`[mutation error] ${error.code} (status ${error.status})`, error);
      }
      toast.error(errorText(error));
    });
  };
}
